// Registry: какие префиксы / точные имена в localStorage и sessionStorage
// относятся к текущей user-session и обязаны быть стёрты при logout.
//
// Источник: docs/audit/2026-05-28-localstorage-leak-audit.md (Wave 1).
// Issue #3 — главный канал утечки `mentor_application_ls_draft` без
// namespace и без cleanup. Эта таблица — single source of truth: добавил
// сюда новый префикс → clearAllSessionData() автоматически его подхватит.
//
// Правила деления:
//   - SESSION_NAMESPACE_PREFIXES — всё, что относится к данной сессии
//     пользователя (drafts, wizard, flow-flags). Стирается на logout.
//   - SESSION_KEEP_KEYS — analytics consent + auth flag. Эти живут не по
//     сессии аккаунта, а по согласию устройства / mutation clearAuthFlag,
//     поэтому централизованный cleanup их пропускает.
//
// Policy: см. `docs/architecture/adr/0003-localstorage-session-policy.md`
// для категоризации (per-user / session-scoped / device-anonymous) и
// чек-листа размещения новых ключей.

/**
 * Префиксы ключей, которые при logout удаляются из localStorage / sessionStorage.
 * Любой ключ, чьё имя начинается с одного из этих префиксов (или точно
 * совпадает с ним), считается session-scoped.
 *
 * Покрытие (по аудиту 2026-05-28 + Wave 4 расширение):
 *   - `platforma_draft_v1_*`       — useFormDraft (create-event, create-consultation, review-text, become-mentor)
 *   - `mentor_application_*`       — raw drafts BecomeMentorForm (`mentor_application_ls_draft`)
 *   - `mentor_wizard_*`            — MentorWelcomeWizard (`done`, `step`, `payment_chosen`)
 *   - `welcome_intent_*`           — WelcomeIntent (`welcome_intent_done`)
 *   - `review_*`                   — Wave 4a: legacy `review_text` + любые будущие review-flags
 *   - `previewEvent`               — Wave 4b: legacy localStorage запись (новый канал — sessionStorage)
 *   - `coachmark_*`                — Coachmark seen-флаги (UI-pref, но привязан к флоу)
 *   - `preflight_ok:*`             — PreflightPage per-booking gate
 *   - `conference-materials:*`     — ZoomCallPage hasJoinedBefore gate
 *   - `pending_payment_*`          — PaymentModal → PaymentReturn handoff
 *   - `event-join-prompt:*`        — EventInfoPage prompt-seen
 *   - `tbank_replace_card_state_*` — PaymentSettings round-trip
 *   - `booking_resume`             — E39: контекст брони на время сбора телефона
 *
 * Сознательно НЕ включены (см. SESSION_KEEP_KEYS и ADR-0003 §Decision):
 *   - `platforma_has_auth`                — чистится напрямую через clearAuthFlag().
 *   - `plf_visitor_id`, `plf_session_state`, `plf_analytics_*`,
 *     `analytics_consent`, `cookie_consent_analytics`
 *                                          — device-policy (consent), Wave 5.
 *   - `creator-landing-theme`, `admin.sidenav.collapsed`, `admin.recent.entities`
 *                                          — UI-prefs устройства, не личности.
 */
export const SESSION_NAMESPACE_PREFIXES: readonly string[] = [
    // drafts (PII)
    'platforma_draft_v1_',
    'mentor_application_',
    // Wave 4a: legacy `review_text` и любые будущие `review_*` флаги.
    // Новый canonical bucket — `platforma_draft_v1_review-text__<uid>`,
    // он покрывается префиксом `platforma_draft_v1_` выше. Этот префикс —
    // safety-net на случай старых записей в браузерах, которые ещё не
    // дообновились до новой версии фронта.
    'review_',
    // wizard / onboarding flow
    'mentor_wizard_',
    'welcome_intent_',
    // UI/flow flags (per-flow gates that should reset between accounts)
    'coachmark_',
    'preflight_ok:',
    'conference-materials:',
    // payment round-trip (sessionStorage in practice, listed for completeness)
    'pending_payment_',
    'tbank_replace_card_state_',
    'event-join-prompt:',
    // E39: снимок брони (слот/тип/вопрос менти) на время шага «укажите телефон».
    // Содержит текст пользователя → чужому аккаунту в этом браузере не место.
    'booking_resume',
    // Wave 4b: legacy previewEvent. Новый канал — sessionStorage через
    // previewEventStorage helper, но если запись осталась от прошлой
    // версии фронта, централизованный cleanup её сносит.
    'previewEvent',
] as const;

/**
 * Ключи, которые НЕ стираются централизованным cleanup'ом. Они живут по
 * device-policy (consent, политика приватности), не по аккаунту.
 *
 * Источник: `frontend/src/shared/lib/consent.ts` —
 *   `ANALYTICS_CONSENT_KEY` = 'plf_analytics_consent'
 *   `ANALYTICS_POLICY_VERSION_KEY` = 'plf_analytics_policy_version'
 *   legacy: 'analytics_consent', 'cookie_consent_analytics'
 *
 * Visitor / session IDs (`plf_visitor_id`, `plf_session_state`) — отдельный
 * Wave 5 (см. аудит §5). На текущий момент их жизнь регулируется
 * withdrawConsent(), не logout'ом.
 */
export const SESSION_KEEP_KEYS: readonly string[] = [
    'plf_analytics_consent',
    'plf_analytics_policy_version',
    'plf_visitor_id',
    'plf_session_state',
    'analytics_consent',
    'cookie_consent_analytics',
] as const;
